import React, { useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { EntitiesTableList } from '../../../components';
import { fetchHealthOrganizations, disableHO, clearHOInfo } from '../../../store/health_organizations';
import { resendInvitationForUser, sendNewPassword, loginAsDifferentRole } from '../../../store/authorization';
import { renderColumns, transformData } from './renderColumns';
import { URLS } from '../../../constants/constants';
import { redirectToPage } from '../../../utils/helpers';
import { usePagination } from '../../../hooks/usePagination';
import { useSearch } from '../../../hooks/useSearch';
import { Navigation } from '../../../utils/navigation';

const Health_Organizations = ({
  disableHO,
  fetchHealthOrganizations,
  limit,
  currentPage,
  total,
  totalPages,
  healthOrganizations, 
  sendNewPassword,
  resendInvitationForUser,
  clearHOInfo,
  isEnterprise,
  loginAsDifferentRole,
  isViewDashboardMode
}) => {
  
  const navigate = Navigation();
  usePagination(navigate);
  const { searchValue, highlighted, handleChangeSearch } = useSearch(fetchHealthOrganizations);

  useEffect(() => {fetchHealthOrganizations({Navigation:navigate,isEnterprise});return;}, [fetchHealthOrganizations]);

  useEffect(() => () => {clearHOInfo(); return;}, [clearHOInfo]);

  const handleManageOrganization = useCallback(
    (id) => () =>  navigate.push(`/health_organizations/${id}/edit`),
    [navigate],
  );

  const switchOrganizationStatus = useCallback((status) => () => disableHO({...status,Navigation:navigate}), [disableHO,navigate]);

  const handleSendPassword = useCallback((id) => () => sendNewPassword(id), [sendNewPassword]);

  const handleResendInvitation = useCallback((id) => () => resendInvitationForUser(id), [resendInvitationForUser]);

  const handleChangePage = (page) => {
    redirectToPage(URLS.HEALTH_ORGANIZATIONS, page,navigate);
    fetchHealthOrganizations({...searchValue,Navigation:navigate,isEnterprise:isEnterprise});
  };

  const handleViewDashboard = useCallback(
    (viewDashboardParams) => () => loginAsDifferentRole({...viewDashboardParams,Navigation:navigate}),
    [loginAsDifferentRole],
  );

  // Handle row clicks - modified to handle tree data
  const handleRowClick = useCallback(
    (record) => () => {
      // If the row has children, we don't want to navigate when clicking
      // Let the row expansion handle it instead
      if (record.children && record.children.length > 0) {
        return;
      }
      
      // For rows without children, navigate to edit page
     
    },
    [navigate]
  );


    const handleAddClinic = (parentOrgId) => () => {
  // Navigate to Create Health Organization page with parentOrgId
  navigate.push(`/health_organizations/new?parentOrgId=${parentOrgId}`);
  
};

  const tableData = useMemo(() => {
    return transformData({
      data: healthOrganizations,
      handleManageOrganization,
      switchOrganizationStatus,
      handleSendPassword,
      handleResendInvitation,
      handleViewDashboard,
      isEnterpriseAdmin: isEnterprise,
      handleAddClinic,
      isViewDashboardMode
    });
  }, [
    healthOrganizations,
    handleManageOrganization,
    switchOrganizationStatus,
    handleSendPassword,
    handleViewDashboard,
    handleResendInvitation,
  ]);

  const columns = useMemo(() => {
    return healthOrganizations?.length > 0 ? renderColumns(searchValue, highlighted) : null;
  }, [healthOrganizations, highlighted, searchValue]);
  
  // Check if data has tree structure
  const hasTreeData = useMemo(() => {
    return tableData?.some(item => item.children && item.children.length > 0);
  }, [tableData]);

  // Configure expandable props for the tree table
  const expandableConfig = useMemo(() => {
    if (!hasTreeData) return null;
    
    return {
      defaultExpandAllRows: false,
      expandRowByClick: true,
      indentSize: 20,
    };
  }, [hasTreeData]);

  return (
    <EntitiesTableList
      title='Organizations'
      addNewBtnTitle='Add New Organization' 
      Navigation={navigate}
      columns={columns}
      data={tableData}
      handleChangePage={handleChangePage}
      handleRowClick={handleRowClick}
      showAddNewButton
      handleChangeSearch={handleChangeSearch}
      searchValue={searchValue}
      limit={limit}
      currentPage={currentPage}
      total={total}
      totalPages={totalPages}
      // Add expandable configuration for tree structure
      expandable={expandableConfig}
    />
  );
};

const mapStateToProps = (state) => ({
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  isEnterprise: state.authorization.isEnterprise,
  limit: state.healthOrganizations.limit,
  currentPage: state.healthOrganizations.currentPage || 1,
  total: state.healthOrganizations.total,
  totalPages: state.healthOrganizations.totalPages,
  isViewDashboardMode: state?.authorization?.viewDashboardMode?.active,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchHealthOrganizations,
      disableHO,
      sendNewPassword,
      clearHOInfo,
      resendInvitationForUser,
      loginAsDifferentRole,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Health_Organizations);
